/**
 * Community Info Section
 * Single-column layout with expandable/collapsible sections
 */

import {
  ChevronDown,
  ChevronUp,
  EllipsisVertical,
  Flag,
  Globe,
  Instagram,
  Link2,
  LockKeyhole,
  MapPin,
  Pencil,
  Upload,
  XCircle,
} from "lucide-react"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { GuestAction } from "@/components/guest"
import {
  BookmarkFilledIcon,
  BookmarkIcon,
  FacebookIcon,
  JoinedCommunityIcon,
  LeaveCommunityIcon,
  RequestSentIcon,
  XIcon,
} from "@/components/icons"
import { ReportModal } from "@/components/shared/ReportModal"
import { ShareModal } from "@/components/shared/ShareModal"
import { TagChip } from "@/components/shared/TagChip"
import { Avatar } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { LoginModal } from "@/features/auth/components/LoginModal"
import { useToggleCommunitySave } from "@/features/profile/hooks/useSavedCommunities"
import { useIsMobile } from "@/hooks/useIsMobile"
import { useTranslation } from "@/language"
import { cn, extractSocialHandle } from "@/lib"
import { useCommunityJoinActions } from "../hooks/useCommunityMembership"
import type { CommunityDetail } from "../types/community-detail"
import { CommunityJoinButton } from "./CommunityJoinButton"
import { CommunityMembersListSheet } from "./communityMembersListSheet"

interface CommunityInfoSectionProps {
  community: CommunityDetail
  currentUserId?: string
  isGuest?: boolean
  onViewMembers?: () => void
}

export function CommunityInfoSection({
  community,
  currentUserId,
  isGuest = false,
  onViewMembers,
}: CommunityInfoSectionProps) {
  const { t } = useTranslation("communities")
  const [isExpanded, setIsExpanded] = useState(true)
  const [guidelinesOpen, setGuidelinesOpen] = useState(false)
  const [membersSheetOpen, setMembersSheetOpen] = useState(false)
  const [showReportModal, setShowReportModal] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)
  const navigate = useNavigate()
  const toggleCommunitySave = useToggleCommunitySave()
  const { leave, cancelRequest, isLeaving, isCancelling } = useCommunityJoinActions()

  // Check if community was created within last 24 hours
  const isNewCommunity = (createdAt?: string) => {
    if (!createdAt) return false
    const created = new Date(createdAt)
    const now = new Date()
    const hoursDiff = (now.getTime() - created.getTime()) / (1000 * 60 * 60)
    return hoursDiff <= 24
  }

  const isCreator = currentUserId === community.createdBy
  const isOwner = community.permissions?.isOwner ?? false
  const isMobile = useIsMobile()

  // Helper to render action buttons based on creator status
  const renderActionButtons = () => {
    if (isCreator || isOwner) {
      // Creator: Joined label + Edit + Bookmark + Share + Hamburger
      return (
        <>
          <div className="flex items-center gap-2 h-10 px-4">
            <JoinedCommunityIcon className="size-5 text-brand-olive" />
            <span className="text-[14px] leading-[20px] font-semibold font-monteserat uppercase">
              Joined
            </span>
          </div>
          <Button
            variant="outline-gradient"
            size="icon"
            className="h-11 w-11 rounded-md border-gradient-3d bg-white/5"
            onClick={handleEditClick}
          >
            <Pencil className="size-5 text-charcoal" />
          </Button>
          <Button
            variant="outline-gradient"
            size="icon"
            className="h-11 w-11 rounded-md border-gradient-3d bg-white/5"
            onClick={handleBookmarkToggle}
          >
            {community.isSaved ? (
              <BookmarkFilledIcon className="size-5 text-charcoal" />
            ) : (
              <BookmarkIcon className="size-5 text-charcoal" />
            )}
          </Button>
          {isGuest ? (
            <GuestAction>
              <Button
                variant="outline-gradient"
                size="icon"
                className="h-11 w-11 rounded-md border-gradient-3d bg-white/5"
              >
                <Upload className="size-[22px] text-off-white" />
              </Button>
            </GuestAction>
          ) : (
            <Button
              variant="outline-gradient"
              size="icon"
              className="h-11 w-11 rounded-md border-gradient-3d bg-white/5"
              onClick={handleShare}
            >
              <Upload className="size-[22px] text-off-white" />
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline-gradient"
                size="icon"
                className="h-11 w-11 rounded-md border-gradient-3d bg-white/5"
              >
                <EllipsisVertical className="size-5 text-charcoal" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-lg p-3">
              <DropdownMenuItem onClick={handleCopyLink} className="cursor-pointer">
                <Link2 className="mr-2 size-5" />
                {t("communityHeader.copyLink")}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleReport}
                className="text-brand-terracotta cursor-pointer"
              >
                <Flag className="mr-2 size-4" />
                {t("communityHeader.report")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      )
    }

    // Non-creator: Join + Share + Hamburger
    return (
      <>
        {!community.isJoined &&
          !community.isPending &&
          (isGuest ? (
            <GuestAction>
              <Button variant="primary" size="sm" className="h-10 rounded-sm px-15 rounded-full">
                {t("joinButton.join")}
              </Button>
            </GuestAction>
          ) : (
            <CommunityJoinButton
              communityId={community.id}
              communityType={community.type}
              variant="primary"
              size="sm"
              className="h-10 rounded-sm px-15 rounded-full"
              initialStatus={{
                isJoined: community.isJoined ?? false,
                isPending: community.isPending ?? false,
              }}
              isOwner={isOwner}
            />
          ))}
        {community.isPending && (
          <div className="flex items-center gap-2 h-10 px-4">
            <RequestSentIcon className="size-5" />
            <span className="text-[14px] leading-[20px] font-semibold font-monteserat uppercase">
              {t("joinButton.requested")}
            </span>
          </div>
        )}
        {community.isJoined && (
          <div className="flex items-center gap-2 h-10 px-4">
            <JoinedCommunityIcon className="size-5 text-brand-olive" />
            <span className="text-[14px] leading-[20px] font-semibold font-monteserat uppercase">
              Joined
            </span>
          </div>
        )}
        {isGuest ? (
          <GuestAction>
            <Button
              variant="outline-gradient"
              size="icon"
              className="h-11 w-11 rounded-md border-gradient-3d bg-white/5"
            >
              {community.isSaved ? (
                <BookmarkFilledIcon className="size-5 text-charcoal" />
              ) : (
                <BookmarkIcon className="size-5 text-charcoal" />
              )}
            </Button>
          </GuestAction>
        ) : community.isJoined ? (
          <Button
            variant="outline-gradient"
            size="icon"
            className="h-11 w-11 rounded-md border-gradient-3d bg-white/5"
            onClick={handleBookmarkToggle}
          >
            {community.isSaved ? (
              <BookmarkFilledIcon className="size-5 text-charcoal" />
            ) : (
              <BookmarkIcon className="size-5 text-charcoal" />
            )}
          </Button>
        ) : null}
        <Button
          variant="outline-gradient"
          size="icon"
          className={`h-11 w-11 border-gradient-3d ${isGuest ? "bg-white/5" : "bg-card"} rounded-md`}
          onClick={handleShare}
        >
          <Upload className="size-4 text-charcoal" />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline-gradient"
              size="icon"
              className="h-11 w-11 border-gradient-3d bg-card rounded-md"
            >
              <EllipsisVertical className="size-4 text-charcoal" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-lg p-3">
            <DropdownMenuItem onClick={handleCopyLink} className="cursor-pointer">
              <Link2 className="mr-2 size-5" />
              {t("communityHeader.copyLink")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleReport}
              className="text-brand-terracotta cursor-pointer"
            >
              <Flag className="mr-2 size-5" />
              {t("communityHeader.report")}
            </DropdownMenuItem>
            {community.isJoined && (
              <DropdownMenuItem
                onClick={handleLeave}
                disabled={isLeaving}
                className="text-brand-terracotta cursor-pointer"
              >
                <LeaveCommunityIcon className="mr-2 size-5" />
                {t("communityHeader.leaveCommunity")}
              </DropdownMenuItem>
            )}
            {community.isPending && (
              <DropdownMenuItem
                onClick={handleCancelRequest}
                disabled={isCancelling}
                className="text-brand-terracotta cursor-pointer"
              >
                <XCircle className="mr-2 size-4" />
                {t("communityHeader.cancelRequest")}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </>
    )
  }

  const handleShare = () => {
    setShareOpen(true)
  }

  const handleEditClick = () => {
    navigate(`/communities/edit/${community.id}`)
  }

  const handleBookmarkToggle = () => {
    toggleCommunitySave.mutate(community.id, {
      onSuccess: data => {
        if (data.saved) {
          toast.success("Community saved")
        } else {
          toast.success("Community unsaved")
        }
      },
      onError: error => {
        toast.error("Failed to update save status")
        console.error("Save community error:", error)
      },
    })
  }

  const handleCopyLink = async () => {
    const communityUrl = `${window.location.origin}/communities/${community.slug || community.id}`
    await navigator.clipboard.writeText(communityUrl)
    toast.success(t("communityHeader.linkCopied"))
  }

  const [guestLoginOpen, setGuestLoginOpen] = useState(false)

  const handleReport = () => {
    if (isGuest) {
      setGuestLoginOpen(true)
      return
    }
    setShowReportModal(true)
  }

  const handleLeave = () => {
    leave(community.id)
  }

  const handleCancelRequest = () => {
    cancelRequest(community.id)
  }

  // Render member avatars (up to 3 overlapping)
  const renderMemberAvatars = () => {
    const members = community.members ?? []
    if (members.length === 0) return null

    return (
      <div className="flex items-center gap-2.5 md:gap-5">
        <div className="flex -space-x-3">
          {members.slice(0, 3).map(member => (
            <button
              key={member.userId}
              type="button"
              onClick={() => navigate(`/profile/${member.userId}`)}
              className="cursor-pointer"
              aria-label={`View ${member.name}'s profile`}
            >
              <Avatar
                src={member.avatarUrl}
                fallback={member.name}
                size="lg"
                className="w-7.5 h-7.5 md:w-10 md:h-10 ring-1 ring-white/30"
              />
            </button>
          ))}
        </div>
        <button
          type="button"
          className="text-sm text-primary font-semibold cursor-pointer"
          onClick={onViewMembers}
        >
          {community.memberCount} Members
        </button>
      </div>
    )
  }

  // Collapsed view: just name + handle row
  if (!isExpanded) {
    return (
      <div className="">
        {/* Header row: Name + Actions */}
        <div className="flex flex-col md:flex-row items-start justify-between">
          <div className="flex-1 w-full">
            <div className="flex items-center">
              <h1 className="text-lg leading-7.5 md:text-[28px] font-monteserat uppercase font-semibold">
                {community.name}
              </h1>
              {isNewCommunity(community.createdAt) && (
                <span className="ml-3 bg-brand-olive border border-brand-olive text-white uppsercase px-4 flex items-center py-0 h-[30px] rounded-full text-[12px] font-monteserat font-semibold">
                  Just Launched!
                </span>
              )}
            </div>
            <div className="flex gap-4 mt-1.25 md:mt-2">
              <p className="text-[13px] leading-5.5 md:text-[14px] text-primary">
                @{community.handle}{" "}
              </p>
              {community.isJoined && !isCreator && !isOwner && isMobile ? (
                <div className="flex items-center gap-1">
                  <span className="text-[13px] leading-5.5 md:text-[14px] md:font-semibold">
                    Joined
                  </span>
                </div>
              ) : community.type === "PRIVATE" ? (
                <p className="flex gap-1 text-[13px] leading-5.5 md:text-[14px] items-center justify-center">
                  {" "}
                  <LockKeyhole className="size-4" /> <span>Private Community</span>
                </p>
              ) : (
                <p className="flex gap-1 text-[13px] leading-5.5 md:text-[14px] items-center justify-center">
                  {" "}
                  <Globe className="size-4" /> <span>Everyone can join</span>
                </p>
              )}
            </div>
            {!community.isJoined && !community.isPending && !isCreator && !isOwner && (
              <div className="mt-5 md:hidden w-full">
                {isGuest ? (
                  <GuestAction>
                    <Button
                      variant="primary"
                      size="sm"
                      className="h-12.5 text-sm font-semibold rounded-full w-full"
                    >
                      {t("joinButton.join")}
                    </Button>
                  </GuestAction>
                ) : (
                  <CommunityJoinButton
                    communityId={community.id}
                    communityType={community.type}
                    variant="primary"
                    size="sm"
                    className="h-12.5 text-sm font-semibold rounded-full w-full"
                    initialStatus={{
                      isJoined: community.isJoined ?? false,
                      isPending: community.isPending ?? false,
                    }}
                    isOwner={isOwner}
                  />
                )}
              </div>
            )}
            {community.isPending && !isCreator && !isOwner && (
              <div className="mt-5 md:hidden w-full">
                <div className="flex items-center gap-2 h-10">
                  <RequestSentIcon className="size-5" />
                  <span className="text-[14px] leading-[20px] font-semibold font-monteserat uppercase">
                    {t("joinButton.requested")}
                  </span>
                </div>
              </div>
            )}
          </div>
          <div className="w-full md:w-auto md:order-last order-first mb-5 md:mb-0 hidden md:flex items-center justify-end gap-2">
            {renderActionButtons()}
          </div>
        </div>

        {/* Show less button */}
        <div className="flex justify-start mt-5 cursor-pointer">
          <span
            onClick={() => setIsExpanded(true)}
            className="flex gap-1.25 text-primary font-semibold text-sm"
          >
            Show less
            <ChevronUp className="size-5" />
          </span>
        </div>
      </div>
    )
  }

  // Expanded view: full layout
  return (
    <div className="flex flex-col gap-5 md:gap-6">
      {/* Header row: Name + Actions */}
      <div className="flex flex-col md:flex-row items-start justify-between">
        <div className="flex-1 w-full">
          <div className="flex items-center">
            <h1 className="text-lg leading-7.5 md:text-[28px] font-monteserat uppercase font-semibold">
              {community.name}
            </h1>
            {isNewCommunity(community.createdAt) && (
              <span className="ml-3 bg-brand-olive border border-brand-olive text-white uppsercase px-4 flex items-center py-0 h-[30px] rounded-full text-[12px] font-monteserat font-semibold">
                Just Launched!
              </span>
            )}
          </div>
          <div className="flex gap-4 mt-1.25 md:mt-2">
            <p className="text-[13px] leading-5.5 md:text-[14px] text-primary">
              @{community.handle}{" "}
            </p>
            {community.isJoined && !isCreator && !isOwner && isMobile ? (
              <div className="flex items-center gap-1">
                <span className="text-[13px] leading-5.5">Joined</span>
              </div>
            ) : community.type === "PRIVATE" ? (
              <p className="flex gap-1 text-[13px] leading-5.5 md:text-[14px] items-center justify-center">
                {" "}
                <LockKeyhole className="size-4" /> <span>Private Community</span>
              </p>
            ) : (
              <p className="flex gap-1 text-[13px] leading-5.5 md:text-[14px] items-center justify-center">
                {" "}
                <Globe className="size-4" /> <span>Everyone can join</span>
              </p>
            )}
          </div>
          {!community.isJoined && !community.isPending && !isCreator && !isOwner && (
            <div className="mt-5 md:hidden w-full">
              {isGuest ? (
                <GuestAction>
                  <Button variant="primary" size="sm" className="h-10 rounded-full w-full">
                    {t("joinButton.join")}
                  </Button>
                </GuestAction>
              ) : (
                <CommunityJoinButton
                  communityId={community.id}
                  communityType={community.type}
                  variant="primary"
                  size="sm"
                  className="h-10 rounded-full w-full"
                  initialStatus={{
                    isJoined: community.isJoined ?? false,
                    isPending: community.isPending ?? false,
                  }}
                  isOwner={isOwner}
                />
              )}
            </div>
          )}
          {community.isPending && !isCreator && !isOwner && (
            <div className="mt-5 md:hidden w-full">
              <div className="flex items-center gap-2 h-10">
                <RequestSentIcon className="size-5" />
                <span className="text-[14px] leading-[20px] font-semibold font-monteserat uppercase">
                  {t("joinButton.requested")}
                </span>
              </div>
            </div>
          )}
        </div>
        <div className="w-full md:w-auto md:order-last order-first mb-5 md:mb-0 hidden md:flex items-center justify-end gap-2">
          {renderActionButtons()}
        </div>
      </div>

      {/* Members */}
      <div className="flex flex-wrap items-center md:mt-1">{renderMemberAvatars()}</div>

      {/* Location */}
      {community.location && (
        <div className="flex flex-wrap items-center gap-2.5 md:gap-1 md:mt-1 text-sm text-stone">
          <MapPin className="size-5 font-semibold" />
          <span className="leading-5.5 md:font-semibold">{community.location}</span>
        </div>
      )}

      {/* Description */}
      <div className="space-y-6 md:mt-2">
        {community.description.split("\n\n").map((paragraph, index) => (
          <div key={index}>
            {paragraph.includes("\n") ? (
              <ul className="space-y-2 list-none">
                {paragraph
                  .split("\n")
                  .filter(line => line.trim())
                  .map((line, lineIndex) => (
                    <li
                      key={lineIndex}
                      className="text-[14px] leading-[25px] text-primary font-monteserat font-normal"
                    >
                      {line}
                    </li>
                  ))}
              </ul>
            ) : (
              <p className="text-[14px] leading-[25px] text-primary font-monteserat font-normal">
                {paragraph}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Social links */}
      {(community.websiteUrl ||
        community.twitterHandle ||
        community.instagramHandle ||
        community.facebookUrl) && (
        <div className="flex flex-col md:flex-row md:flex-wrap md:items-center gap-5 md:gap-10 md:mt-2">
          {community.websiteUrl && (
            <a
              href={
                community.websiteUrl.startsWith("http")
                  ? community.websiteUrl
                  : `https://${community.websiteUrl}`
              }
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2.5 md:gap-2 text-sm text-primary font-semibold"
            >
              <Link2 className="size-5.5" />
              <span>{community.websiteUrl}</span>
            </a>
          )}
          {community.twitterHandle && (
            <a
              href={`https://twitter.com/${community.twitterHandle.replace(/^@/, "")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2.5 md:gap-2 text-sm text-primary font-semibold"
            >
              <XIcon className="size-5.5" />
              <span>{extractSocialHandle(community.twitterHandle) || "Twitter"}</span>
            </a>
          )}
          {community.instagramHandle && (
            <a
              href={
                community.instagramHandle.startsWith("http")
                  ? community.instagramHandle
                  : `https://instagram.com/${community.instagramHandle.replace(/^@/, "")}`
              }
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2.5 md:gap-2 text-sm text-primary font-semibold"
            >
              <Instagram className="size-5.5" />
              <span>{extractSocialHandle(community.instagramHandle) || "Instagram"}</span>
            </a>
          )}
          {community.facebookUrl && (
            <a
              href={
                community.facebookUrl.startsWith("http")
                  ? community.facebookUrl
                  : `https://${community.facebookUrl}`
              }
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2.5 md:gap-2 text-sm text-primary font-semibold"
            >
              <FacebookIcon className="size-5.5" />
              <span>{extractSocialHandle(community.facebookUrl) || "Facebook"}</span>
            </a>
          )}
        </div>
      )}

      {/* Tags */}
      {community.tags.length > 0 && (
        <div className="flex flex-wrap gap-2.5 md:gap-3 md:mt-1">
          {community.tags.map(tag => {
            const tagText = typeof tag === "string" ? tag : tag.tagText
            return (
              <TagChip
                key={tagText}
                tag={tagText}
                tab="communities"
                className="p-2.5 md:p-3 rounded-lg text-[13px] leading-5 md:rounded-md bg-transparent border border-white text-white"
              />
            )
          })}
        </div>
      )}

      {/* Guidelines Accordion */}
      {community.guidelines && (
        <div>
          <button
            onClick={() => setGuidelinesOpen(!guidelinesOpen)}
            className="flex md:mt-1 gap-1.25 md:gap-2 items-center justify-center"
          >
            <span className="text-sm font-semibold text-primary">{t("info.guidelines")}</span>
            <ChevronDown
              className={cn(
                "size-5 text-charcoal transition-transform cursor-pointer",
                guidelinesOpen && "rotate-180",
              )}
            />
          </button>
          {guidelinesOpen && (
            <div className="md:mt-1 rounded-b-lg pt-3 md:py-3">
              {community.guidelines.includes("\n") ? (
                <ul className="space-y-2 list-none">
                  {community.guidelines
                    .split("\n")
                    .filter(line => line.trim())
                    .map((line, index) => (
                      <li key={index} className="text-sm text-stone">
                        {line}
                      </li>
                    ))}
                </ul>
              ) : (
                <p className="text-sm text-stone">{community.guidelines}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Show less button */}
      <div className="flex justify-start md:mt-2 cursor-pointer">
        <span
          onClick={() => setIsExpanded(false)}
          className="flex gap-1 text-primary font-semibold text-[14px]"
        >
          Show less
          <ChevronUp className="size-5" />
        </span>
      </div>

      {/* Members List Sheet */}
      <CommunityMembersListSheet
        communityId={community.id}
        isOpen={membersSheetOpen}
        onClose={() => setMembersSheetOpen(false)}
        totalMemberCount={community.memberCount}
      />

      {/* Report Modal */}
      <ReportModal
        open={showReportModal}
        onOpenChange={setShowReportModal}
        target="COMMUNITY"
        targetData={{
          id: community.id,
          name: community.name,
          handle: community.handle,
          imageUrl: community.avatarUrl || community.coverImages?.[0] || null,
        }}
      />

      <ShareModal
        open={shareOpen}
        onOpenChange={setShareOpen}
        shareUrl={`${window.location.origin}/communities/${community.slug || community.id}`}
        shareText={`Check out ${community.name} on Rituality`}
        shareType="community"
        shareImageUrl={community.coverImages?.[0] || community.avatarUrl || null}
        displayPath={`/communities/${community.name.toLowerCase().replace(/\s+/g, "-")}`}
      />

      {isGuest && <LoginModal open={guestLoginOpen} onOpenChange={setGuestLoginOpen} />}
    </div>
  )
}
